import React, { useEffect, useRef, useState } from "react";
import { FaBullseye, FaGrip, FaList, FaMagnifyingGlass, FaPaste } from "react-icons/fa6";
import { SOURCES, hasUsableWebsite } from "../appShared";
import DetailPanel from "./DetailPanel";
import LeadCard, { getLeadCategory } from "./LeadCard";

export default function PipelineTab({ leads, allLeadCount, hasMore, isLoadingMore, onLoadMore, setTab, selected, setSelected, pipeFilter, setPipeFilter, pipeSource, setPipeSource, pipeSearch, setPipeSearch, pipeQuickFilter, setPipeQuickFilter, viewMode, setViewMode, uniqueSources, sourceCounts, detailTab, setDetailTab, updateLead, deleteLead, findEmail, crawlEmail, crawlProjectDetails, qualifyWithAI, aiQualifying, generateOutreach, outreachLoading, outreachMsg, setOutreachMsg, setConfirmModal, gmailStatus, sendEmail, searching, OutreachTabComponent }) {
  const PAGE_STEP = 15;
  const gridRef = useRef(null);
  const loadLockRef = useRef(false);
  const scrollDebounceRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_STEP);
  const [categoryFilter, setCategoryFilter] = useState("All Categories");
  const [completeOnly, setCompleteOnly] = useState(false);
  const filteredLeads = leads
    .filter((lead) => categoryFilter === "All Categories" || getLeadCategory(lead).label === categoryFilter)
    .filter((lead) => !completeOnly || !!(lead.phone || lead.email || hasUsableWebsite(lead)));
  const visibleLeads = filteredLeads.slice(0, visibleCount);
  const displayStart = visibleLeads.length > 0 ? 1 : 0;
  const displayEnd = visibleLeads.length;

  useEffect(() => {
    if (leads.length === 0) {
      setVisibleCount(PAGE_STEP);
      return;
    }
    setVisibleCount((prev) => {
      if (prev < PAGE_STEP) return Math.min(PAGE_STEP, leads.length);
      if (prev > leads.length) return leads.length;
      return prev;
    });
  }, [leads.length]);

  useEffect(() => {
    if (!isLoadingMore) loadLockRef.current = false;
  }, [isLoadingMore]);

  useEffect(() => {
    const gridEl = gridRef.current;
    if (!gridEl) return;

    const onScroll = () => {
      if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current);
      scrollDebounceRef.current = setTimeout(() => {
        const distanceFromBottom = gridEl.scrollHeight - (gridEl.scrollTop + gridEl.clientHeight);

        if (distanceFromBottom <= 180) {
          if (visibleCount < leads.length) {
            setVisibleCount((prev) => Math.min(prev + PAGE_STEP, leads.length));
            return;
          }

          if (!loadLockRef.current && hasMore && !isLoadingMore) {
            loadLockRef.current = true;
            onLoadMore();
          }
        }
      }, 120);
    };

    gridEl.addEventListener("scroll", onScroll, { passive: true });

    if (gridEl.scrollHeight <= gridEl.clientHeight + 10) {
      if (visibleCount < leads.length) {
        setVisibleCount((prev) => Math.min(prev + PAGE_STEP, leads.length));
      } else if (!loadLockRef.current && hasMore && !isLoadingMore) {
        loadLockRef.current = true;
        onLoadMore();
      }
    }

    return () => {
      gridEl.removeEventListener("scroll", onScroll);
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
        scrollDebounceRef.current = null;
      }
    };
  }, [hasMore, isLoadingMore, onLoadMore, leads.length, visibleCount]);

  return (
    <div className="pipeline-layout">
      <div className="filter-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            className="search-input"
            placeholder="Search name, company, city, industry…"
            value={pipeSearch}
            onChange={(event) => setPipeSearch(event.target.value)}
            disabled={searching}
          />
        </div>
        <select className="filter-pill" value={pipeFilter} onChange={(event) => setPipeFilter(event.target.value)} disabled={searching}>
          {["All Statuses", "New", "Contacted", "Interested", "Won", "Lost"].map((status) => <option key={status}>{status}</option>)}
        </select>
        <select className="filter-pill" value={pipeSource} onChange={(event) => setPipeSource(event.target.value)} disabled={searching}>
          <option>All Sources</option>
          {uniqueSources.map((source) => <option key={source}>{source}</option>)}
        </select>
        <select className="filter-pill" value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)} disabled={searching}>
          {["All Categories", "AI & ML", "MOBILE APP", "WEB DEV", "SOFTWARE DEV", "IT CONSULTING", "AGENCY", "OTHER"].map((cat) => <option key={cat}>{cat}</option>)}
        </select>
        <button
          type="button"
          className="filter-pill"
          onClick={() => setCompleteOnly((prev) => !prev)}
          disabled={searching}
          title="Hide leads with no phone, email, or website"
          style={completeOnly ? { background: "#4f46e5", borderColor: "#4f46e5", color: "#fff" } : undefined}
        >
          {completeOnly ? "\u2713 " : ""}Complete leads only
        </button>
        <div className="view-toggle">
          <button className={`view-btn ${viewMode === "grid" ? "active" : ""}`} onClick={() => setViewMode("grid")} disabled={searching}><FaGrip /></button>
          <button className={`view-btn ${viewMode === "list" ? "active" : ""}`} onClick={() => setViewMode("list")} disabled={searching}><FaList /></button>
        </div>
        {pipeQuickFilter && <button className="filter-pill active" style={{ background: "#ef4444", borderColor: "#ef4444" }} onClick={() => setPipeQuickFilter("")} disabled={searching}>✕ Clear filter</button>}
        <span className="result-count">{displayStart}-{displayEnd} of {allLeadCount}</span>
      </div>

      {/* Source breakdown strip */}
      {sourceCounts && Object.keys(sourceCounts).length > 0 && (
        <div className="source-breakdown-bar">
          <span className="source-breakdown-label">Data from:</span>
          {Object.entries(sourceCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([src, count]) => {
              const entry = SOURCES.find((s) => s.label === src);
              const isCountryFlag = entry && !["OPP", "DOFFIN"].includes(entry.id);
              const isActive = pipeSource === src;
              return (
                <button
                  key={src}
                  className={`source-pill${isActive ? " active" : ""}`}
                  onClick={() => setPipeSource(isActive ? "All Sources" : src)}
                  title={isActive ? `Clear filter` : `Show only ${src} leads`}
                  disabled={searching}
                >
                  {isCountryFlag ? (
                    <img
                      src={`https://flagcdn.com/16x12/${entry.id.toLowerCase()}.png`}
                      alt=""
                      className="source-pill-flag"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  ) : (
                    <span className="source-pill-icon">
                      {entry?.id === "OPP" ? "🌐" : entry?.id === "DOFFIN" ? "📋" : "📍"}
                    </span>
                  )}
                  <span className="source-pill-name">{src}</span>
                  <span className="source-pill-count">{count}</span>
                </button>
              );
            })}
        </div>
      )}

      <div className="pipeline-body">
        <div ref={gridRef} className={`leads-grid ${viewMode}`}>
          {leads.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon"><FaBullseye /></div>
              <div className="empty-title">No leads yet</div>
              <div className="empty-sub">Search businesses across 15+ countries to get started</div>
              <div style={{ display: "flex", gap: 12, marginTop: 20, justifyContent: "center" }}>
                <button onClick={() => setTab("find")} style={{ background: "#1D9E75", color: "white", border: "none", borderRadius: 10, padding: "12px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                  <FaMagnifyingGlass /> Find Leads
                </button>
                <button onClick={() => setTab("paste")} style={{ background: "#f8fafc", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                  <FaPaste /> Paste
                </button>
              </div>
            </div>
          )}
          {visibleLeads.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              selected={selected?.id === lead.id}
              onClick={() => { setSelected(lead); setDetailTab("info"); }}
              viewMode={viewMode}
              searching={searching}
            />
          ))}
          {(visibleCount < leads.length || hasMore) && (
            <div className="pipeline-load-more">
              <div className="pipeline-load-more-text">
                {visibleCount < leads.length
                  ? `Scroll to load next ${Math.min(PAGE_STEP, leads.length - visibleCount)} leads...`
                  : (isLoadingMore ? "Loading more leads..." : "Scroll to load more leads...")}
              </div>
              <button
                className="btn-load-more"
                type="button"
                onClick={() => {
                  if (visibleCount < leads.length) {
                    setVisibleCount((prev) => Math.min(prev + PAGE_STEP, leads.length));
                    return;
                  }
                  if (!isLoadingMore && hasMore) onLoadMore();
                }}
                disabled={isLoadingMore}
              >
                {visibleCount < leads.length
                  ? `Load next ${Math.min(PAGE_STEP, leads.length - visibleCount)}`
                  : (isLoadingMore ? "Loading..." : "Load more")}
              </button>
            </div>
          )}
        </div>
      </div>

      {selected && (
        <div className="detail-modal-overlay">
          <div className="detail-modal-shell">
            <DetailPanel
              lead={selected}
              detailTab={detailTab}
              setDetailTab={setDetailTab}
              updateLead={updateLead}
              deleteLead={deleteLead}
              findEmail={findEmail}
              crawlEmail={crawlEmail}
              qualifyWithAI={qualifyWithAI}
              crawlProjectDetails={crawlProjectDetails}
              aiQualifying={aiQualifying}
              generateOutreach={generateOutreach}
              outreachLoading={outreachLoading}
              outreachMsg={outreachMsg}
              setOutreachMsg={setOutreachMsg}
              gmailStatus={gmailStatus}
              sendEmail={sendEmail}
              onClose={() => setSelected(null)}
              setConfirmModal={setConfirmModal}
              searching={searching}
              OutreachTabComponent={OutreachTabComponent}
            />
          </div>
        </div>
      )}
    </div>
  );
}